import { createBrowserRouter, redirect, useRouteError } from 'react-router-dom';
import { lazy, Suspense, useState } from 'react';
import NotFound from './pages/NotFound';
import {
    AdditionalPayResultType,
    createAccountByToken,
    CreateOrderSheetReturnType,
    ExtraChagreType,
    getMallUserIdByToken,
    GroupPurchaseOrderType,
    Inquiries,
    MallBusinessInfoType,
    MallInfo,
    MallNotification,
    MallUserInfoType,
    Mileage,
    OnlineCouponListType,
    OrderDetail,
    OrderDetailV2,
    ProductDetailsType,
    ShippingAddressBookType,
    UserCouponListType,
    UserOrder,
    UserOrderV2,
} from './services/mallUser.service';

import { axiosApi } from './helpers/api.helper';
import {
    checkIsMallUser,
    checkRefreshToken,
    checkRefreshTokenBool,
    getTokenByAnonymous,
    isLogined,
    logOut,
    setReferrerRefreshToken,
    setReferrerToken,
} from './services/auth.service';
import { getProductDetail } from './services/matching.service';
import { queryClient } from './lib/queryClient';
import { vendorQueryOptions } from './hooks/useVendorInfo';
import { productListQueryOptions } from './hooks/useProductList';
import { PurchaseDrawerStoreState } from './store/purchaseDrawerStore';
import { useSourcingReferrerStore } from './store/sourcingReferrerStore';
import isMobile from 'is-mobile';
import { useAnonymousUserStore, useUserStore } from './store/userStore';
import { useVendorAuthStore } from './store/vendorAuthStore';
import { useShopStore } from './store/shopStore';
import { isIncompatibleVendorLogin } from './helpers/vendorRoleGuard';
import { decodeJwtPayload, loginWithVendorToken, loginWithToken } from './services/auth.service';

// Lazy load pages (로딩 시간 최적화)
const DDMHome = lazy(() => import('./pages/DDM/Home/DDMHome'));
const Main2 = lazy(() => import('./pages/DDM/Home/MainPage2'));
const MainPageMobile = lazy(() => import('./pages/DDM/Home/MainPageMobile'));
const LandingRenewal = lazy(() => import('./pages/DDM/Home/LandingRenewal'));
const EventTryPage = lazy(() => import('./pages/DDM/Event/EventTryPage'));
const CampaignWelcomePage = lazy(() => import('./pages/DDM/Event/CampaignWelcomePage'));
const EventTryUnsignPage = lazy(() => import('./pages/DDM/Event/EventTryUnsignPage'));
const AdminShop3Page = lazy(() => import('./pages/DDM/Admin/AdminShop3Page'));
const FashnTestPage = lazy(() => import('./pages/DDM/Admin/FashnTestPage'));
const EventResultPage = lazy(() => import('./pages/DDM/Event/EventResultPage'));
const ProductDetailPage = lazy(() => import('./pages/DDM/Product/ProductDetailPage'));
const CheckoutPage = lazy(() => import('./pages/DDM/Checkout/CheckoutPage'));
const OrderComplete = lazy(() => import('./pages/DDM/Checkout/OrderComplete'));
const OrdersPage = lazy(() => import('./pages/DDM/Orders/OrdersPage'));
const ResellerOrderDetail = lazy(() => import('./pages/DDM/Orders/ResellerOrderDetail'));
const ReturnsPage = lazy(() => import('./pages/DDM/Orders/ReturnsPage'));
const BackorderPage = lazy(() => import('./pages/DDM/Orders/BackorderPage'));
const SamplePage = lazy(() => import('./pages/DDM/Orders/SamplePage'));
const CartPage = lazy(() => import('./pages/DDM/Cart/CartPage'));
const MypagePage = lazy(() => import('./pages/DDM/Mypage/MypagePage'));
const VendorRegister = lazy(() => import('./pages/DDM/Auth/VendorRegister'));
const AccountSettingsPage = lazy(() => import('./pages/DDM/Account/AccountSettingsPage'));
const VendorSettingsPage = lazy(() => import('./pages/DDM/Settings/VendorSettingsPage'));
const SubscriptionSettingsPage = lazy(() => import('./pages/DDM/Settings/SubscriptionSettingsPage'));
const SocialLoginComponent = lazy(() => import('./pages/DDM/Auth/SocialLogin'));
const VendorSocialLoginComponent = lazy(() => import('./pages/DDM/Auth/VendorSocialLogin'));
const BillingReturnHandler = lazy(() =>
    import('./pages/DDM/Billing/BillingReturnHandler').then((m) => ({ default: m.BillingReturnHandler })),
);
const SubscriptionPage = lazy(() => import('./pages/DDM/Subscription/SubscriptionPage'));
const SubscriptionCheckoutPage = lazy(() => import('./pages/DDM/Subscription/SubscriptionCheckoutPage'));
const SubscriptionSuccessPage = lazy(() => import('./pages/DDM/Subscription/SubscriptionSuccessPage'));
const CashChargePage = lazy(() => import('./pages/DDM/Cash/CashChargePage'));
const CashHistoryPage = lazy(() => import('./pages/DDM/Cash/CashHistoryPage'));
const DirectOrderPage = lazy(() => import('./pages/DDM/DirectOrder/DirectOrderPage'));
const DepositHistoryPage = lazy(() => import('./pages/DDM/Deposit/DepositHistoryPage'));
const LikeListPage = lazy(() => import('./pages/DDM/Like/LikeListPage'));
const Shop3PlansPage = lazy(() => import('./pages/DDM/Subscription/Shop3PlansPage'));
const Shop3SubscriptionManagePage = lazy(() => import('./pages/DDM/Subscription/Shop3SubscriptionPage'));

const env = import.meta.env.VITE_SET_ENV || 'DEV';

async function ensureVendorData(vendorPath: string) {
    if (!vendorPath) return null;
    const vendor = await queryClient.ensureQueryData(vendorQueryOptions(vendorPath));
    // queryFn이 캐시 hit로 스킵되면 shopStore가 세팅되지 않으므로 항상 동기화
    if (vendor) {
        useShopStore.getState().setShop(vendor);
    }
    return vendor;
}

async function ensureProductListData(params: {
    vendorId: string;
    tab?: string;
}) {
    if (!params.vendorId) return null;
    return queryClient.ensureQueryData(productListQueryOptions(params));
}

const router = createBrowserRouter([
    {
        path: '/',
        element: <LandingRenewal />,
        errorElement: <ErrorComponent />,
    },
    {
        // 기존 랜딩 보존 (리뉴얼 이전 페이지)
        path: '/legacy',
        element: isMobile() ? <MainPageMobile /> : <Main2 />,
        errorElement: <ErrorComponent />,
    },
    {
        path: '/mobile',
        element: <MainPageMobile />,
        errorElement: <ErrorComponent />,
    },
    {
        path: '/:vendorPath',
        element: <DDMHome />,
        id: 'vendorRoute',
        errorElement: <ErrorComponent />,
        loader: async ({ params }) => {
            try {
                const loaderStart = performance.now();

                await sessionCheckFunc();
                console.log(`[DDMHome loader] sessionCheckFunc: ${(performance.now() - loaderStart).toFixed(0)}ms`);

                const { vendorPath } = params;
                const vendorStart = performance.now();
                const vendor = await ensureVendorData(vendorPath || '');
                console.log(`[DDMHome loader] ensureVendorData: ${(performance.now() - vendorStart).toFixed(0)}ms`);
                if (!vendor) {
                    return redirect('/');
                }
                const productStart = performance.now();
                const productList = await ensureProductListData({ vendorId: vendor.id });
                console.log(`[DDMHome loader] productLoader: ${(performance.now() - productStart).toFixed(0)}ms`);

                console.log(`[DDMHome loader] TOTAL: ${(performance.now() - loaderStart).toFixed(0)}ms`);
                return {
                    vendor,
                    productList,
                };
            } catch (error) {
                console.error('[/:vendorPath loader] error:', error);
                const { vendorPath } = params;
                const vendor = await ensureVendorData(vendorPath || '').catch(() => null);
                if (!vendor) return redirect('/');
                return { vendor, productList: null };
            }
        },
    },
    {
        path: '/:vendorPath/products/:productGroupId',
        element: <ProductDetailPage />,
        errorElement: <ErrorComponent />,
        loader: async ({ params }) => {
            try {
                await sessionCheckFunc();

                const { vendorPath, productGroupId } = params;
                const vendor = await ensureVendorData(vendorPath || '');
                if (!vendor) {
                    return redirect('/');
                }

                const product = await getProductDetail({ productGroupId: productGroupId || '' });
                if (!product) {
                    return redirect(`/${vendorPath}`);
                }
                return product;
            } catch (error) {
                console.error('[product detail loader] error:', error);
                const { vendorPath } = params;
                return redirect(`/${vendorPath || '/'}`);
            }
        },
    },
    {
        path: '/:vendorPath/all',
        element: <DDMHome />,
        errorElement: <ErrorComponent />,
        loader: async ({ params }) => {
            try {
                const { vendorPath } = params;
                const vendor = await ensureVendorData(vendorPath || '');
                if (!vendor) {
                    return redirect('/');
                }
                const productList = await ensureProductListData({ vendorId: vendor.id });
                return { vendor, productList };
            } catch (error) {
                console.error('[/all loader] error:', error);
                const { vendorPath } = params;
                const vendor = await ensureVendorData(vendorPath || '').catch(() => null);
                if (!vendor) return redirect('/');
                return { vendor, productList: null };
            }
        },
    },
    {
        path: '/:vendorPath/new',
        element: <DDMHome />,
        errorElement: <ErrorComponent />,
        loader: async ({ params }) => {
            try {
                const { vendorPath } = params;
                const vendor = await ensureVendorData(vendorPath || '');
                if (!vendor) {
                    return redirect('/');
                }
                const productList = await ensureProductListData({ vendorId: vendor.id, tab: 'new' });
                return { vendor, productList };
            } catch (error) {
                console.error('[/new loader] error:', error);
                const { vendorPath } = params;
                const vendor = await ensureVendorData(vendorPath || '').catch(() => null);
                if (!vendor) return redirect('/');
                return { vendor, productList: null };
            }
        },
    },
    {
        path: '/:vendorPath/best-new',
        element: <DDMHome />,
        errorElement: <ErrorComponent />,
        loader: async ({ params }) => {
            try {
                const { vendorPath } = params;
                const vendor = await ensureVendorData(vendorPath || '');
                if (!vendor) {
                    return redirect('/');
                }
                const productList = await ensureProductListData({ vendorId: vendor.id, tab: 'bestNew' });
                return { vendor, productList };
            } catch (error) {
                console.error('[/best-new loader] error:', error);
                const { vendorPath } = params;
                const vendor = await ensureVendorData(vendorPath || '').catch(() => null);
                if (!vendor) return redirect('/');
                return { vendor, productList: null };
            }
        },
    },
    {
        path: '/:vendorPath/today-new',
        element: <DDMHome />,
        errorElement: <ErrorComponent />,
        loader: async ({ params }) => {
            try {
                const { vendorPath } = params;
                const vendor = await ensureVendorData(vendorPath || '');
                if (!vendor) {
                    return redirect('/');
                }
                const productList = await ensureProductListData({ vendorId: vendor.id, tab: 'todayNew' });
                return { vendor, productList };
            } catch (error) {
                console.error('[/today-new loader] error:', error);
                const { vendorPath } = params;
                const vendor = await ensureVendorData(vendorPath || '').catch(() => null);
                if (!vendor) return redirect('/');
                return { vendor, productList: null };
            }
        },
    },
    {
        path: '/:vendorPath/discount',
        element: <DDMHome />,
        errorElement: <ErrorComponent />,
        loader: async ({ params }) => {
            try {
                const { vendorPath } = params;
                const vendor = await ensureVendorData(vendorPath || '');
                if (!vendor) {
                    return redirect('/');
                }
                const productList = await ensureProductListData({ vendorId: vendor.id, tab: 'discount' });
                return { vendor, productList };
            } catch (error) {
                console.error('[/discount loader] error:', error);
                const { vendorPath } = params;
                const vendor = await ensureVendorData(vendorPath || '').catch(() => null);
                if (!vendor) return redirect('/');
                return { vendor, productList: null };
            }
        },
    },
    {
        path: '/:vendorPath/sold-out',
        element: <DDMHome />,
        errorElement: <ErrorComponent />,
        loader: async ({ params }) => {
            try {
                const { vendorPath } = params;
                const vendor = await ensureVendorData(vendorPath || '');
                if (!vendor) {
                    return redirect('/');
                }
                const productList = await ensureProductListData({ vendorId: vendor.id, tab: 'soldOut' });
                return { vendor, productList };
            } catch (error) {
                console.error('[/sold-out loader] error:', error);
                const { vendorPath } = params;
                const vendor = await ensureVendorData(vendorPath || '').catch(() => null);
                if (!vendor) return redirect('/');
                return { vendor, productList: null };
            }
        },
    },
    {
        path: '/:vendorPath/cart',
        element: <CartPage />,
        errorElement: <ErrorComponent />,
        loader: async ({ params }) => {
            try {
                await sessionCheckFunc();
                const { vendorPath } = params;
                const vendor = await ensureVendorData(vendorPath || '');
                if (!vendor) return redirect('/');
                return { vendor };
            } catch (error) {
                console.error('[/cart loader] error:', error);
                const { vendorPath } = params;
                const vendor = await ensureVendorData(vendorPath || '').catch(() => null);
                if (!vendor) return redirect('/');
                return { vendor };
            }
        },
    },
    {
        path: '/:vendorPath/vendor-register',
        element: <VendorRegister />,
        errorElement: <ErrorComponent />,
        loader: async ({ params }) => {
            try {
                const { vendorPath } = params;
                const vendor = await ensureVendorData(vendorPath || '');
                if (!vendor) return redirect('/');
                return { vendor };
            } catch (error) {
                console.error('[/vendor-register loader] error:', error);
                return redirect('/');
            }
        },
    },
    {
        path: '/:vendorPath/checkout',
        element: <CheckoutPage />,
        errorElement: <ErrorComponent />,
        loader: async ({ params }) => {
            try {
                await sessionCheckFunc();
                const { vendorPath } = params;
                const vendor = await ensureVendorData(vendorPath || '');
                if (!vendor) return redirect('/');
                return { vendor };
            } catch (error) {
                console.error('[/checkout loader] error:', error);
                const { vendorPath } = params;
                const vendor = await ensureVendorData(vendorPath || '').catch(() => null);
                if (!vendor) return redirect('/');
                return { vendor };
            }
        },
    },
    {
        path: '/:vendorPath/order-complete/:orderId',
        element: <OrderComplete />,
        errorElement: <ErrorComponent />,
        loader: async ({ params }) => {
            try {
                await sessionCheckFunc();
                const { vendorPath } = params;
                const vendor = await ensureVendorData(vendorPath || '');
                if (!vendor) return redirect('/');
                return { vendor };
            } catch (error) {
                console.error('[/order-complete loader] error:', error);
                const { vendorPath } = params;
                const vendor = await ensureVendorData(vendorPath || '').catch(() => null);
                if (!vendor) return redirect('/');
                return { vendor };
            }
        },
    },
    {
        path: '/:vendorPath/orders',
        element: <OrdersPage />,
        errorElement: <ErrorComponent />,
        loader: async ({ params }) => {
            try {
                await sessionCheckFunc();
                const { vendorPath } = params;
                const vendor = await ensureVendorData(vendorPath || '');
                if (!vendor) return redirect('/');
                return { vendor };
            } catch (error) {
                console.error('[/orders loader] error:', error);
                const { vendorPath } = params;
                const vendor = await ensureVendorData(vendorPath || '').catch(() => null);
                if (!vendor) return redirect('/');
                return { vendor };
            }
        },
    },
    {
        path: '/:vendorPath/orders/:orderId',
        element: <ResellerOrderDetail />,
        errorElement: <ErrorComponent />,
        loader: async ({ params }) => {
            try {
                await sessionCheckFunc();
                const { vendorPath } = params;
                const vendor = await ensureVendorData(vendorPath || '');
                if (!vendor) return redirect('/');
                return { vendor };
            } catch (error) {
                console.error('[/orders/:orderId loader] error:', error);
                const { vendorPath } = params;
                const vendor = await ensureVendorData(vendorPath || '').catch(() => null);
                if (!vendor) return redirect('/');
                return { vendor };
            }
        },
    },
    {
        path: '/:vendorPath/returns',
        element: <ReturnsPage />,
        errorElement: <ErrorComponent />,
        loader: async ({ params }) => {
            try {
                await sessionCheckFunc();
                const { vendorPath } = params;
                const vendor = await ensureVendorData(vendorPath || '');
                if (!vendor) return redirect('/');
                return { vendor };
            } catch (error) {
                console.error('[/returns loader] error:', error);
                const { vendorPath } = params;
                const vendor = await ensureVendorData(vendorPath || '').catch(() => null);
                if (!vendor) return redirect('/');
                return { vendor };
            }
        },
    },
    {
        path: '/:vendorPath/backorder',
        element: <BackorderPage />,
        errorElement: <ErrorComponent />,
        loader: async ({ params }) => {
            try {
                await sessionCheckFunc();
                const { vendorPath } = params;
                const vendor = await ensureVendorData(vendorPath || '');
                if (!vendor) return redirect('/');
                return { vendor };
            } catch (error) {
                console.error('[/backorder loader] error:', error);
                const { vendorPath } = params;
                const vendor = await ensureVendorData(vendorPath || '').catch(() => null);
                if (!vendor) return redirect('/');
                return { vendor };
            }
        },
    },
    {
        path: '/:vendorPath/sample',
        element: <SamplePage />,
        errorElement: <ErrorComponent />,
        loader: async ({ params }) => {
            try {
                await sessionCheckFunc();
                const { vendorPath } = params;
                const vendor = await ensureVendorData(vendorPath || '');
                if (!vendor) return redirect('/');
                return { vendor };
            } catch (error) {
                console.error('[/sample loader] error:', error);
                const { vendorPath } = params;
                const vendor = await ensureVendorData(vendorPath || '').catch(() => null);
                if (!vendor) return redirect('/');
                return { vendor };
            }
        },
    },
    {
        path: '/:vendorPath/mypage',
        element: <MypagePage />,
        errorElement: <ErrorComponent />,
        loader: async ({ params }) => {
            try {
                await sessionCheckFunc();
                const { vendorPath } = params;
                const vendor = await ensureVendorData(vendorPath || '');
                if (!vendor) return redirect('/');
                return { vendor };
            } catch (error) {
                console.error('[/mypage loader] error:', error);
                const { vendorPath } = params;
                const vendor = await ensureVendorData(vendorPath || '').catch(() => null);
                if (!vendor) return redirect('/');
                return { vendor };
            }
        },
    },
    {
        path: '/:vendorPath/settings',
        element: <VendorSettingsPage />,
        errorElement: <ErrorComponent />,
        loader: async ({ params }) => {
            try {
                await sessionCheckFunc();
                const { vendorPath } = params;
                const vendor = await ensureVendorData(vendorPath || '');
                if (!vendor) return redirect('/');
                return { vendor };
            } catch (error) {
                console.error('[/settings loader] error:', error);
                const { vendorPath } = params;
                const vendor = await ensureVendorData(vendorPath || '').catch(() => null);
                if (!vendor) return redirect('/');
                return { vendor };
            }
        },
    },
    {
        path: '/:vendorPath/settings/subscription',
        element: <SubscriptionSettingsPage />,
        errorElement: <ErrorComponent />,
        loader: async ({ params }) => {
            try {
                await sessionCheckFunc();
                const { vendorPath } = params;
                const vendor = await ensureVendorData(vendorPath || '');
                if (!vendor) return redirect('/');
                return { vendor };
            } catch (error) {
                console.error('[/settings/subscription loader] error:', error);
                const { vendorPath } = params;
                const vendor = await ensureVendorData(vendorPath || '').catch(() => null);
                if (!vendor) return redirect('/');
                return { vendor };
            }
        },
    },
    {
        path: '/:vendorPath/cash/charge',
        element: <CashChargePage />,
        errorElement: <ErrorComponent />,
        loader: async ({ params }) => {
            try {
                await sessionCheckFunc();
                const { vendorPath } = params;
                const vendor = await ensureVendorData(vendorPath || '');
                if (!vendor) return redirect('/');
                return { vendor };
            } catch (error) {
                console.error('[/cash/charge loader] error:', error);
                const { vendorPath } = params;
                const vendor = await ensureVendorData(vendorPath || '').catch(() => null);
                if (!vendor) return redirect('/');
                return { vendor };
            }
        },
    },
    {
        path: '/:vendorPath/cash/history',
        element: <CashHistoryPage />,
        errorElement: <ErrorComponent />,
        loader: async ({ params }) => {
            try {
                await sessionCheckFunc();
                const { vendorPath } = params;
                const vendor = await ensureVendorData(vendorPath || '');
                if (!vendor) return redirect('/');
                return { vendor };
            } catch (error) {
                console.error('[/cash/history loader] error:', error);
                const { vendorPath } = params;
                const vendor = await ensureVendorData(vendorPath || '').catch(() => null);
                if (!vendor) return redirect('/');
                return { vendor };
            }
        },
    },
    {
        path: '/:vendorPath/deposit/history',
        element: <DepositHistoryPage />,
        errorElement: <ErrorComponent />,
        loader: async ({ params }) => {
            try {
                await sessionCheckFunc();
                const { vendorPath } = params;
                const vendor = await ensureVendorData(vendorPath || '');
                if (!vendor) return redirect('/');
                return { vendor };
            } catch (error) {
                console.error('[/deposit/history loader] error:', error);
                const { vendorPath } = params;
                const vendor = await ensureVendorData(vendorPath || '').catch(() => null);
                if (!vendor) return redirect('/');
                return { vendor };
            }
        },
    },
    {
        path: '/:vendorPath/direct-order',
        element: <DirectOrderPage />,
        errorElement: <ErrorComponent />,
        loader: async ({ params }) => {
            try {
                await sessionCheckFunc();
                const { vendorPath } = params;
                const vendor = await ensureVendorData(vendorPath || '');
                if (!vendor) return redirect('/');
                return { vendor };
            } catch (error) {
                console.error('[/direct-order loader] error:', error);
                const { vendorPath } = params;
                const vendor = await ensureVendorData(vendorPath || '').catch(() => null);
                if (!vendor) return redirect('/');
                return { vendor };
            }
        },
    },
    {
        path: '/:vendorPath/likes',
        element: <LikeListPage />,
        errorElement: <ErrorComponent />,
        loader: async ({ params }) => {
            try {
                await sessionCheckFunc();
                const { vendorPath } = params;
                const vendor = await ensureVendorData(vendorPath || '');
                if (!vendor) return redirect('/');
                return { vendor };
            } catch (error) {
                console.error('[/likes loader] error:', error);
                const { vendorPath } = params;
                const vendor = await ensureVendorData(vendorPath || '').catch(() => null);
                if (!vendor) return redirect('/');
                return { vendor };
            }
        },
    },
    {
        path: '/:vendorPath/subscription/plans',
        element: <Shop3PlansPage />,
        errorElement: <ErrorComponent />,
        loader: async ({ params }) => {
            try {
                await sessionCheckFunc();
                const { vendorPath } = params;
                const vendor = await ensureVendorData(vendorPath || '');
                if (!vendor) return redirect('/');
                return { vendor };
            } catch (error) {
                console.error('[/subscription/plans loader] error:', error);
                const { vendorPath } = params;
                const vendor = await ensureVendorData(vendorPath || '').catch(() => null);
                if (!vendor) return redirect('/');
                return { vendor };
            }
        },
    },
    {
        path: '/:vendorPath/subscription/manage',
        element: <Shop3SubscriptionManagePage />,
        errorElement: <ErrorComponent />,
        loader: async ({ params }) => {
            try {
                await sessionCheckFunc();
                const { vendorPath } = params;
                const vendor = await ensureVendorData(vendorPath || '');
                if (!vendor) return redirect('/');
                return { vendor };
            } catch (error) {
                console.error('[/subscription/manage loader] error:', error);
                const { vendorPath } = params;
                const vendor = await ensureVendorData(vendorPath || '').catch(() => null);
                if (!vendor) return redirect('/');
                return { vendor };
            }
        },
    },
    {
        path: '/try',
        element: <EventTryPage />,
        errorElement: <ErrorComponent />,
    },
    {
        // 재유입 캠페인 문자 링크 랜딩. /welcome 은 /:vendorPath 에 잡혀 상품 페이지로 가므로 두 단계 경로
        path: '/users/welcome',
        element: <CampaignWelcomePage />,
        errorElement: <ErrorComponent />,
    },
    {
        // 공식 주소(/try)는 건드리지 않고, 생성 엔진을 바꿔가며 시험하는 전용 주소.
        // 운영에도 올려서 실제 환경으로 비교하고 기록은 어드민에 함께 남는다.
        path: '/try_unsign',
        element: <EventTryUnsignPage />,
        errorElement: <ErrorComponent />,
    },
    {
        // 운영자 전용 (비밀번호는 서버가 검증)
        path: '/admin_shop3',
        element: <AdminShop3Page />,
        errorElement: <ErrorComponent />,
    },
    {
        // FASHN 가상피팅 시험대. 검증이 끝나면 지우거나 본 서비스로 옮긴다
        path: '/develop_fashionAI',
        element: <FashnTestPage />,
        errorElement: <ErrorComponent />,
    },
    {
        path: '/event/:token',
        element: <EventResultPage />,
        errorElement: <ErrorComponent />,
    },
    {
        path: '/subscription',
        element: <SubscriptionPage />,
        errorElement: <ErrorComponent />,
    },
    {
        path: '/promo',
        element: <SubscriptionPage />,
        errorElement: <ErrorComponent />,
    },
    {
        path: '/subscription/checkout',
        element: <SubscriptionCheckoutPage />,
        errorElement: <ErrorComponent />,
    },
    {
        path: '/subscription/success',
        element: <SubscriptionSuccessPage />,
        errorElement: <ErrorComponent />,
    },
    {
        path: '/billing/return',
        element: <BillingReturnHandler />,
        errorElement: <ErrorComponent />,
    },
    {
        path: '/account/settings',
        element: <AccountSettingsPage />,
        errorElement: <ErrorComponent />,
    },
    {
        // 도매(vendor) 소셜 가입/로그인 콜백.
        // 아래 social/:socialName 은 mallUser(구매자) 전용이라 섞으면 안 된다.
        path: '/vendor-social/:socialName',
        element: <VendorSocialLoginComponent />,
        errorElement: <ErrorComponent />,
    },
    {
        path: 'social/:socialName',
        element: <SocialLoginComponent />,
        errorElement: <ErrorComponent />,
        loader: async ({ params }) => {
            const { socialName: social } = params;
            const queryString = new URLSearchParams(window.location.search);
            const path = String(queryString.get('state')).split('/')[2];
            try {
                if (isLogined()) {
                    return redirect(!!path ? `/mall/${path}` : `/mall`);
                }
                return { social };
            } catch (error) {
                throw error;
            }
        },
    },
    {
        path: '/app-ads.txt',
        element: <p>google.com, pub-2774361938949385, DIRECT, f08c47fec0942fa0</p>,
    },
]);

export interface PersonalnfoLoaderReturnType {
    userInfo: MallUserInfoType;
    shippingList: ShippingAddressBookType[];
}

export interface MallMainLoader {
    mallInfo: MallInfo['mallInfo'];
    tagNames: string[];
    notificationList: MallNotification['notificationList'];
    mallBusinessInfo: MallBusinessInfoType['mallBusinessInfo'];
    shopName: string;
}
export interface ProductDetailsLoader {
    productDetails: ProductDetailsType;
    drawerProductList: PurchaseDrawerStoreState['drawerProductList'];
    mallInfo: MallInfo['mallInfo'];
    mallBusinessInfo: MallBusinessInfoType['mallBusinessInfo'];
    shopName: string;
}
export interface OrderNPayLoaderReturnType {
    shippingAddressBookList: ShippingAddressBookType[];
    mileageList: Mileage['mileageList'];
    userInfo: MallUserInfoType;
    mallId: string;
    orderInfo: CreateOrderSheetReturnType['orderInfo'];
    couponList: UserCouponListType['items'];
    // salesPolicyInfo: SalesPolicyType,
    state: 'success' | 'fail';
    pgType: string;
    pgBusinessType: string;
    shoppingPoint: number;
    userPoint: number;
    shoppingPointPolicy: {
        id: string;
        policyName: string;
        targetUserType: string;
        pointConversionRate: number;
        maxUsageRate: number;
        minConversionAmount: number;
        minUsageAmount: number;
        isActive: boolean;
        priority: number;
        startDateTime: string;
        endDateTime: string;
    };
}
export interface FooterInfoDataType {
    mallBusinessInfo: MallBusinessInfoType['mallBusinessInfo'];
    shopName: string;
    mallId: string;
    mallInfo: MallInfo['mallInfo'];
}
export interface DestinationListLoaderReturnType {
    shippingAddressBookList: ShippingAddressBookType[];
}
export interface DestinationAdditionLoaderReturnType {
    userInfo: MallUserInfoType;
}
export interface MypageLoaderReturnType {
    userInfo: MallUserInfoType;
    mileage: number;
    mallId: string;
    state: 'success' | 'fail';
}
export interface PersonalnfoLoaderReturnType {
    userInfo: MallUserInfoType;
    shippingList: ShippingAddressBookType[];
}
export interface OrderNShippingInfoReturnType {}
export interface AdditionalPayReturnType {
    extraChargeInfo: ExtraChagreType['extraChargeInfo'];
}
export interface AdditionalPayResultReturnType {
    payResult: AdditionalPayResultType['extraChargeInfo'];
    paramData: {
        accountNumber: string;
        bank: string;
        customerName: string;
        dueDate: string;
        failMessage: string;
    };
}
export interface PayResultReturnType {
    orderDetailInfo: OrderDetailV2['item'];
    paramData: {
        accountNumber: string;
        bank: string;
        customerName: string;
        dueDate: string;
        failMessage: string;
        depositor: string;
    };
    couponList: OnlineCouponListType[];
    pgType: 'TOSS' | 'NICE';
}

/**
 * @description
 * `비회원일 경우 api_key를 이용해 토큰과 ID를 useAnonymousUserStore에 저장합니다.`
 */
export async function fetchNStoreAnonymousTokenNId() {
    const { reset: userReset } = useUserStore.getState();
    /* 비회원일 경우 토큰 발급 */
    const isAnonymousToken = useAnonymousUserStore.getState().accessToken ? true : false;
    // if (!isLogined() && !isAnonymousToken) {
    userReset();
    const { access_token } = await getTokenByAnonymous();
    axiosApi.defaults.headers.common.Authorization = `Bearer ${access_token}`;
    const { mallUserId } = await createAccountByToken(access_token);
    useAnonymousUserStore.setState({
        mallUserId,
        accessToken: access_token,
    });
}

async function sessionCheckFunc(notRefer = true) {
    // ?token= 파라미터가 있으면 기존 세션 무시하고 새 토큰으로 로그인
    const urlParams = new URLSearchParams(window.location.search);
    const tokenParam = urlParams.get('token');
    if (tokenParam) {
        // 기존 세션 클리어 (다른 프로젝트에서 남은 stale 토큰 제거)
        useUserStore.getState().reset();
        useVendorAuthStore.getState().reset();
        useAnonymousUserStore.getState().reset();
        delete axiosApi.defaults.headers.common['Authorization'];

        try {
            const payload = decodeJwtPayload(tokenParam);
            if (payload?.user_type === 'VENDOR_MEMBER') {
                await loginWithVendorToken(tokenParam);
            } else {
                await loginWithToken(tokenParam);
            }
            // URL에서 토큰 파라미터 제거
            const newUrl = new URL(window.location.href);
            newUrl.searchParams.delete('token');
            window.history.replaceState({}, '', newUrl.toString());
        } catch (error) {
            // 실패 시 anonymous token으로 폴백
            await fetchNStoreAnonymousTokenNId();
        }
        return;
    }

    // 이미 로그인된 경우 세션 체크 스킵
    if (isLogined()) {
        const { accessToken: vendorAccessToken, shopVendorPath } = useVendorAuthStore.getState();
        const currentVendorPath = window.location.pathname.split('/')[1] || '';

        if (vendorAccessToken) {
            // vendorPath가 불일치하면 stale 토큰 제거
            if (shopVendorPath && currentVendorPath && shopVendorPath !== currentVendorPath) {
                useVendorAuthStore.getState().reset();
                // fall through → refresh token 또는 anonymous token flow로 진행
            } else if (isIncompatibleVendorLogin()) {
                // 도매 쇼핑몰에서 도매 사용자 stale 토큰 제거
                useVendorAuthStore.getState().reset();
                // fall through → anonymous token flow로 진행
            } else {
                axiosApi.defaults.headers.common.Authorization = `Bearer ${vendorAccessToken}`;
                return;
            }
        }

        // userStore 토큰이 있으면 (소매 사용자) 그대로 사용
        const { accessToken: userAccessToken } = useUserStore.getState();
        if (userAccessToken) {
            axiosApi.defaults.headers.common.Authorization = `Bearer ${userAccessToken}`;
            return;
        }
    }

    try {
        const isToken = await checkRefreshTokenBool().then((r) => r.data);
        if (isToken) {
            const { reset: anonymousUserReset } = useAnonymousUserStore.getState();
            anonymousUserReset();
            try {
                const { access_token, expires_in } = await checkRefreshToken()
                    .then((r) => r)
                    .catch((err) => {
                        throw err;
                    });

                // 다른 프로젝트에서 남은 vendor 쿠키로 생성된 토큰 차단
                const refreshPayload = decodeJwtPayload(access_token);
                if (refreshPayload?.user_type === 'VENDOR_MEMBER') {
                    await fetchNStoreAnonymousTokenNId();
                    return;
                }

                useUserStore.setState({
                    accessToken: access_token,
                    expiresIn: expires_in,
                });
                axiosApi.defaults.headers.common.Authorization = `Bearer ${access_token}`;
                const isMallUser = await checkIsMallUser(access_token).then((r) => r.data);
                if (isMallUser) {
                    const { mallUserId } = await getMallUserIdByToken(access_token).then((r) => r);
                    useUserStore.setState({ mallUserId });
                } else {
                    const { mallUserId } = await createAccountByToken(access_token).then((r) => r);
                    useUserStore.setState({ mallUserId });
                }
            } catch (err) {
                console.error('Session check failed:', err);
                await logOut();
                window.location.reload();
                return;
            }
        } else {
            await fetchNStoreAnonymousTokenNId();
        }
        if (!notRefer) return;
        const queryString = new URLSearchParams(window.location.search);

        // 출처 - 후킹스프 기여도
        const utm_source = queryString.get('utm_source') || '';
        // 매체 - 쇼츠 피드를 보고 넘어옴
        const utm_medium = queryString.get('utm_medium') || '';
        // 인스타셀러 아이디 or 플로잉 닉네임 - 누가 등록한 영상을 보고 넘어왔는가
        // (크롤링 쇼츠의 경우 sns 아이디, 직접 등록한 경우 플로잉 닉네임)
        const utm_campaign = queryString.get('utm_campaign') || '';
        // 상품 아이디 - 해당 상품 구매에만 referrerToken 사용 가능
        const utm_campaign_id = queryString.get('utm_campaign_id') || '';
        const utm_referrer = queryString.get('utm_referrer') || '';
        const utm_content = queryString.get('utm_content') || '';
        const utm_term = queryString.get('utm_term') || '';

        const referer = queryString.get('referer') || '';
        const user_id = queryString.get('user_id') || '';

        const isValidQuery =
            utm_source ||
            utm_medium ||
            utm_campaign ||
            utm_referrer ||
            referer ||
            utm_campaign_id ||
            user_id ||
            utm_content ||
            utm_term;

        const queryPayload = {
            utm_source,
            utm_medium,
            utm_campaign,
            utm_referrer,
            utm_campaign_id,
            user_id,
            referer,
            utm_content,
            utm_term,
        };

        if (isValidQuery) {
            const token: string =
                (await setReferrerToken({ ...queryPayload })
                    .then((r) => r)
                    .catch(() => '')) || '';
            useSourcingReferrerStore.setState({
                referrer_token: token,
            });
        } else {
            const token: string =
                (await setReferrerRefreshToken({ ...queryPayload })
                    .then((r) => r)
                    .catch(() => '')) || '';
            useSourcingReferrerStore.setState({
                referrer_token: token,
            });
        }
    } catch (error) {
        const err: any = error;
        if (!err.status || !err.statusCode) {
            throw { status: -1 };
        }
        try {
            await logOut();
            window.location.reload();
        } finally {
        }
    }
}
export interface OrderListLoaderType {
    orderList: UserOrder[];
    totalCount: number | null;
    isNextLists: boolean;
    isIssue: 'yes' | '';
}
export interface OrderListLoaderV2Type {
    items: UserOrderV2[];
    totalCount: number | null;
    isNextLists: boolean;
    isIssue: boolean;
}

export interface MyInquiryListLoaderType {
    lists: Inquiries['inquiryList'];
    groupPurchaseList: GroupPurchaseOrderType[];
    totalCount: number;
}

export interface NoticePageLoaderType {
    lists: MallNotification['notificationList'];
}

export interface OrderDetailInfoLoaderType {
    orderDetail: OrderDetail['orderDetailInfo'] | 'null';
    isIsland: boolean;
    isJeju: boolean;
    noData?: boolean;
    couponList: OnlineCouponListType[];
}

export interface OrderDetailInfoLoaderTypeV2 {
    orderDetail: OrderDetailV2['item'] | 'null';
    isIsland: boolean;
    isJeju: boolean;
    noData?: boolean;
    couponList: OnlineCouponListType[];
}

interface Designdata {
    spacehome_create_step: number;
    design_type: string;
    bg_filter_type: string;
    bg_type: string;
    description: string;
    sub_description: null;
    bg_color: string;
    text_color: string;
    button_color: string;
    profile_img_url: string;
    banner_img_url: string;
    bg_img_url: string;
    is_button_animated: boolean;
    is_theme: boolean;
    path: string;
}
export interface SpaceHomeV2LoaderReturnType {
    design_data: Designdata;
    item_list: any;
    user_id: string;
    is_mall_opened: boolean;
    nickname: string;
}

function ErrorComponent() {
    const error: any = useRouteError();
    console.log('error::::', error);
    return <NotFound error={error} />;
}

export default router;
